/**
 * Keeps the default discover feed from running out.
 *
 * The curated decks — a MAHI search, a tapped category — have had a
 * continuation since useDeckContinuation: they top themselves up and climb a
 * widening ladder rather than ending where their exact matches do. The default
 * feed had none. It fetched forty cards once, at mount, and when the shopper
 * reached the end of them the screen said "You're all caught up" — on a
 * catalogue of 1,444. Forty swipes is one sitting.
 *
 * That comment on useDeckContinuation ("the default feed is deliberately not
 * wired through here") was right about the *mechanism* and wrong about the
 * conclusion. The default feed has no request to stay anchored to, so it has
 * nothing to widen away from and the ladder does not apply to it. But having
 * nothing to widen is not the same as having nothing left: what it needs is
 * simply the next page, which is what /inventory/dresses already returns —
 * a fresh shuffle of the whole catalogue minus everything this person has
 * already had a verdict on (routes/mira/inventory.py::list_dresses).
 *
 * So this is the same moving window, without the transition card: there is no
 * substitution to agree to, because nothing has been substituted. The server
 * has already decided what widening this person's preferences needed when it
 * built the batch, and says so through the same `notice` the first fetch used.
 *
 * It stops for exactly one reason: two consecutive batches with nothing new in
 * them. That is the honest version of "you're all caught up" — the catalogue,
 * the recycle window and all, has nothing this person has not judged.
 */
import { useEffect, useRef } from 'react';
import { fetchFreshFeed } from '../../prefetch';

// Same six as useDeckContinuation's REFILL_AT, for the same reason: the stack
// renders three cards and a swipe takes a couple of seconds, so a fetch that
// starts any later than this is visible as an empty stage.
export const REFILL_AT = 6;

// Consecutive batches that turned out to hold nothing new before this gives
// up. Two, not one: a single all-duplicate page is what a shuffle does when a
// swipe from ten seconds ago has not reached the server's own seen-set yet,
// and treating that as the end of the catalogue would dead-end a feed that
// still has hundreds of products in it.
const MAX_BARREN = 2;

export default function useFeedRefill({ active, deck, index, gender, prefsKey,
                                       seenRef, onAppend }) {
  const inFlightRef = useRef(false);
  const barrenRef = useRef(0);
  const doneRef = useRef(false);
  const keyRef = useRef(null);
  const wasActiveRef = useRef(false);

  // Two things clear an exhausted feed, and both are during render rather than
  // in an effect so the refill below sees the cleared state on this same pass
  // instead of one swipe later.
  //
  // A new gender or a new set of preferences is a different catalogue: the
  // deck is rebuilt from scratch upstream, and a `done` carried across would
  // leave the new one unable to refill itself.
  const key = `${gender || ''}|${prefsKey || ''}`;
  if (keyRef.current !== key) {
    keyRef.current = key;
    barrenRef.current = 0;
    doneRef.current = false;
  }
  // And coming back to the feed from a MAHI search: restoreDefaultDeck may
  // have just fetched a genuinely new deck, and however exhausted the previous
  // one was says nothing about this one.
  if (active && !wasActiveRef.current) {
    barrenRef.current = 0;
    doneRef.current = false;
  }
  wasActiveRef.current = active;

  useEffect(() => {
    if (!active || doneRef.current || inFlightRef.current) return;
    if (deck.length - index > REFILL_AT) return;

    inFlightRef.current = true;
    // Not cancelled on cleanup, for the same reason useDeckContinuation is
    // not: every swipe re-runs this effect, so an abandoned fetch would be
    // re-issued on the next card and cost one request per swipe.
    (async () => {
      const { items } = await fetchFreshFeed(gender);
      inFlightRef.current = false;
      if (!items) return;   // request failed — the next swipe tries again

      // Two filters, both needed. The server excludes what it knows this
      // person has judged across every session; `seenRef` covers the verdicts
      // from this one that have not landed there yet, and the deck check stops
      // a card the shuffle happened to deal twice appearing twice.
      const known = new Set(deck.map((p) => p.mira_sku));
      const fresh = items.filter(
        (p) => !known.has(p.mira_sku) && !seenRef.current.has(p.mira_sku));

      if (!fresh.length) {
        barrenRef.current += 1;
        if (barrenRef.current >= MAX_BARREN) doneRef.current = true;
        return;
      }
      barrenRef.current = 0;
      onAppend(fresh);
    })();
    // `deck.length` rather than `deck`: a new array identity of the same
    // length is the same deck as far as running low is concerned.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, deck.length, index, key]);
}
